import React from 'react';
import { Button } from '@finpay-development/shared-components';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Grid, 
  Typography,
  Box,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { PrePayRequirement } from '../utils/pre-pay-validation';

interface PrePayErrorModalProps {
  isOpen: boolean;
  handleModalClose: () => void;
  requirementList: PrePayRequirement[];
  isLoading?: boolean;
  onFixClick?: (url: string) => void;
}

export function PrePayErrorModal(props: PrePayErrorModalProps) {
  const { isOpen, handleModalClose, requirementList, isLoading = false, onFixClick } = props;
  
  const failedRequirements = requirementList.filter((item) => !item.status);

  const handleFixNavigation = (url: string) => {
    if (onFixClick) {
      onFixClick(url);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <Dialog
      scroll="paper"
      className="modal user-modal requirements-modal"
      open={isOpen}
      aria-labelledby="pre-pay-error-dialog-title"
      maxWidth="md"
      test-id="pre-pay-error-modal"
    >
      <DialogTitle 
        className="spa-modal-title"
        sx={{ color: '#1976d2', fontWeight: 500 }}
      >
        Missing Requirements
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction="column" style={{ padding: "1.5rem 0 2rem 0" }}>
          <Typography 
            className="pt-2" 
            style={{ 
              paddingTop: "0", 
              fontSize: "16px", 
              paddingBottom: "20px", 
              fontFamily: "Inter", 
              lineHeight: "25px", 
              fontWeight: "400", 
              color: "#808080" 
            }}
          >
            The following items need to be addressed before proceeding:
          </Typography>
          
          {isLoading && (
            <Grid 
              container 
              direction="row" 
              style={{ 
                fontFamily: 'Inter, sans-serif', 
                fontWeight: "400", 
                fontSize: "16px", 
                padding: "8px 0", 
                alignItems: "center" 
              }}
            >
              <FiberManualRecordIcon 
                style={{ 
                  color: "#df1e44", 
                  border: "1px solid red", 
                  borderRadius: "50%", 
                  paddingTop: "0px", 
                  marginRight: "8px", 
                  fontSize: "12px" 
                }} 
                className="pt-3" 
                fontSize="small"
              />
              <span>Patient data has not fully loaded.</span>
            </Grid>
          )}
          
          {!isLoading && failedRequirements.map((requirement, index) => (
            <Box 
              key={index}
              sx={{
                padding: '8px 0',
              }}
              test-id={`pre-pay-requirement-${index}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FiberManualRecordIcon 
                    style={{ 
                      color: "#df1e44", 
                      border: "1px solid red", 
                      borderRadius: "50%", 
                      paddingTop: "0px", 
                      marginRight: "8px", 
                      fontSize: "12px",
                      flexShrink: 0,
                    }} 
                    className="pt-3" 
                    fontSize="small"
                  />
                  <span 
                    style={{ 
                      fontFamily: 'Inter, sans-serif', 
                      fontWeight: "400", 
                      fontSize: "16px",
                    }}
                    test-id={`pre-pay-requirement-text-${index}`}
                  >
                    {requirement.requirementName}
                  </span>
                </Box>
                {requirement.fixUrl && (
                  <Button
                    type="text"
                    onClick={() => handleFixNavigation(requirement.fixUrl!)}
                    test-id={`pre-pay-fix-btn-${index}`}
                    style={{
                      textDecoration: 'underline',
                      padding: 0,
                      minWidth: 'auto',
                    }}
                  >
                    {requirement.fixLabel || 'Complete'}
                  </Button>
                )}
              </Box>
              {requirement.missingFields && requirement.missingFields.length > 0 && (
                <Typography
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: '#666',
                    marginLeft: '20px',
                    marginTop: '4px',
                  }}
                  test-id={`pre-pay-missing-fields-${index}`}
                >
                  Missing: {requirement.missingFields.join(', ')}
                </Typography>
              )}
            </Box>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions style={{ borderTop: "", marginTop: "0", padding: "2rem" }}>
        <Button onClick={handleModalClose} test-id="pre-pay-error-modal-close-btn">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PrePayErrorModal;
